
import { useAppSelector } from "@/hooks/useAppSelector";
import { ChartContainer, ChartTooltip } from "@/components/ui/chart";
import { BarChart, Bar, XAxis, YAxis } from "recharts";
import { EVENT_CHANGES } from "@/utils/eventLabels";
import { useState, useMemo } from "react";
import FilterDropdown from "@/components/FilterDropdown";

export const EventsDistributionGraph = () => {
  const { events } = useAppSelector(state => state.events);
  const [selectedCategories, setSelectedCategories] = useState<string[]>(['All']);

  // Get unique categories from events
  const uniqueCategories = useMemo(() => {
    const categories = events.map(event => event.category).filter(Boolean);
    return ['All', ...Array.from(new Set(categories))];
  }, [events]);

  // Filter events based on selected categories
  const filteredEvents = useMemo(() => {
    if (selectedCategories.includes('All')) return events;
    return events.filter(event => event.category && selectedCategories.includes(event.category));
  }, [events, selectedCategories]);

  // Process events data to count different types of changes
  const eventCounts = filteredEvents.reduce((acc: { [key: string]: number }, event) => {
    if (event.priceIncreased) acc['priceIncrease'] = (acc['priceIncrease'] || 0) + 1;
    if (event.priceDecreased) acc['priceDecrease'] = (acc['priceDecrease'] || 0) + 1;
    if (event.plansChanged) acc['plans'] = (acc['plans'] || 0) + 1;
    if (event.priceAvailbilityChanged) acc['availability'] = (acc['availability'] || 0) + 1;
    if (event.introDiscount) acc['introDiscount'] = (acc['introDiscount'] || 0) + 1;
    if (event.displayChanged) acc['display'] = (acc['display'] || 0) + 1;
    return acc;
  }, {});

  // Convert to array format for Recharts and add labels
  const chartData = Object.entries(eventCounts).map(([type, count]) => ({
    type: EVENT_CHANGES[type as keyof typeof EVENT_CHANGES].label,
    count,
  }));

  // Sort by count in descending order
  chartData.sort((a, b) => (b.count as number) - (a.count as number));

  const handleCategoryChange = (value: string | string[]) => {
    setSelectedCategories(Array.isArray(value) ? value : [value]);
  };

  return (
    <div className="w-full bg-white p-6 rounded-lg shadow-sm">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold text-black">Event Type Distribution</h3>
        <div className="w-[200px]">
          <FilterDropdown
            label="Category"
            options={uniqueCategories}
            value={selectedCategories}
            onChange={handleCategoryChange}
            isMulti={true}
            displayAllText="All Categories"
          />
        </div>
      </div>
      <div className="flex flex-col items-start w-full">
        <div className="w-full h-[400px]">
          <ChartContainer
            config={{
              bar: {
                theme: {
                  light: "#4285f4",
                  dark: "#4285f4",
                },
              },
            }}
          >
            <BarChart 
              data={chartData} 
              margin={{ top: 20, right: 30, left: 30, bottom: 60 }}
              width={800}
              height={400}
            >
              <XAxis
                dataKey="type"
                angle={-45}
                textAnchor="end"
                height={80}
                tick={{ fill: '#000000', fontSize: 12 }}
                stroke="#000000"
              />
              <YAxis
                tick={{ fill: '#000000', fontSize: 12 }}
                stroke="#000000"
                label={{ 
                  value: 'Number of Events', 
                  angle: -90, 
                  position: 'insideLeft', 
                  fill: '#000000'
                }}
              />
              <Bar 
                dataKey="count" 
                fill="#4285f4"
                radius={[4, 4, 0, 0]} 
              />
              <ChartTooltip />
            </BarChart>
          </ChartContainer>
        </div>
      </div>
    </div>
  );
};
